import React from "react";
import { motion } from "framer-motion";
import { Clock, ChefHat, GlassWater, Cake } from "lucide-react";
import { useEnquiry } from "../App";

const BEVERAGES = [
  { name: "Ube Latte", time: "10 min", level: "Cafe Hero", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/5dvpyz3w_IMG_9438.png", desc: "Espresso + steamed milk + Ube powder = the most photographed latte on Instagram. A Mumbai cafe must-have." },
  { name: "Ube Iced Coffee", time: "5 min", level: "Summer Bestseller", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/qdzkhnzv_IMG_9407.png", desc: "Cold-brew layered over Ube cream — that gradient shot that owns Reels every July." },
  { name: "Ube Milkshake", time: "5 min", level: "Crowd-pleaser", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/lcl177ho_IMG_9408.png", desc: "Ube powder + vanilla ice cream + milk — thick, creamy, royal violet. Kids and influencers both order seconds." },
  { name: "Ube Smoothie Bowl", time: "8 min", level: "Healthy Hero", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/r07c5nw9_IMG_9409.png", desc: "Ube + banana + oat milk topped with granola and coconut. The wellness cafe winner." },
  { name: "Ube Frappé", time: "5 min", level: "Cafe Premium", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/cx4uj9sy_IMG_9406.png", desc: "Blended Ube, ice, milk and whipped cream — premium ₹350+ menu hero with insane perceived value." },
  { name: "Ube Hot Chocolate", time: "10 min", level: "Winter Special", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/y8611d02_IMG_9426.jpeg", desc: "Decadent purple hot chocolate with marshmallows — your winter menu's signature drink." },
  { name: "Ube Bubble Tea", time: "15 min", level: "Bestseller", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/hmw2zme4_IMG_9427.jpeg", desc: "Replace boring taro with real Ube. Higher perceived value, vivid colour, premium menu pricing." },
  { name: "Ube Matcha Fusion", time: "8 min", level: "Viral Fusion", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/fxk5s0dk_IMG_9428.jpeg", desc: "The trending purple-green layered drink that's dominating cafe Reels — instant social currency." },
];

const DESSERTS = [
  { name: "Ube Halaya", time: "45 min", level: "Classic", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/038j2ajx_IMG_9439.png", desc: "The iconic purple yam jam — sweet, creamy, served on toast, pandesal, or as the base of every Ube dessert." },
  { name: "Ube Ice Cream", time: "Overnight", level: "Crowd-pleaser", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/svisugur_IMG_9437.png", desc: "Soft, vibrant violet scoops with a vanilla-coconut finish. The #1 viral Ube application worldwide." },
  { name: "Ube Cheesecake", time: "4 hrs", level: "Premium dessert", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/mz7hrsye_IMG_9429.jpeg", desc: "Baked or no-bake, the marbled purple finish makes this a ₹450+ menu hero for premium cafes." },
  { name: "Ube Soft-Serve", time: "Variable", level: "Summer winner", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/mz79tsqc_IMG_9430.jpeg", desc: "Twist with vanilla for a Mr Whippy-style cone in stunning purple. Footfall magnet for kiosks." },
  { name: "Ube Pandesal", time: "3 hrs", level: "Bakery upsell", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/c4jmxcyg_IMG_9431.jpeg", desc: "Soft bread rolls filled with Ube halaya — perfect bakery counter product, high margin, premium presentation." },
  { name: "Ube Kulfi", time: "8 hrs", level: "Indian Fusion", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/rwzczunk_IMG_9432.jpeg", desc: "The most Instagrammable Indian fusion — purple yam kulfi for dessert menus and weddings." },
  { name: "Ube Brownie", time: "1 hr", level: "Bakery Hero", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/qxyxx0rs_IMG_9433.jpeg", desc: "Fudgy chocolate brownie marbled with Ube cream — the most-ordered slice in premium cafes." },
  { name: "Ube Macarons", time: "3 hrs", level: "Patisserie Premium", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/f4heqffk_IMG_9434.jpeg", desc: "Perfect violet shells with Ube ganache — luxury patisserie product at ₹120+ each." },
  { name: "Ube Donuts", time: "2 hrs", level: "Cafe Bestseller", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/caztgi0v_IMG_9436.jpeg", desc: "Glazed in royal purple, dusted with gold — the donut shop centrepiece worth queuing for." },
  { name: "Ube Tres Leches", time: "5 hrs", level: "Hotel Pastry", img: "https://customer-assets.emergentagent.com/job_ube-trade-india/artifacts/7qw6ixj6_IMG_9440.png", desc: "Soaked sponge with Ube-infused three milks — a hotel banquet showstopper dessert." },
];

function RecipeCard({ r, i }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
      className="surface-card overflow-hidden"
      data-testid={`recipe-${r.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#F3EFEA]">
        <img src={r.img} alt={r.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
      </div>
      <div className="p-6">
        <h3 className="font-display text-2xl text-[#4B0082] mb-2">{r.name}</h3>
        <div className="flex items-center gap-4 text-xs text-[#888] uppercase tracking-[0.15em] mb-3">
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {r.time}</span>
          <span className="inline-flex items-center gap-1"><ChefHat size={12} /> {r.level}</span>
        </div>
        <p className="text-[#555] leading-relaxed">{r.desc}</p>
      </div>
    </motion.article>
  );
}

export default function Recipes() {
  const { open: openEnquiry } = useEnquiry();
  return (
    <div className="bg-[#FCFAF8]" data-testid="recipes-page">
      <section className="pt-16 pb-12 md:pt-24 md:pb-16 bg-gradient-to-b from-[#F3EFEA] to-[#FCFAF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <div className="eyebrow mb-3">Recipes & Applications</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#1A1A1A] mb-5">
            What you can <em className="text-[#4B0082]">create</em> with our Ube.
          </h1>
          <p className="text-lg text-[#555] max-w-2xl mx-auto">
            18+ high-margin applications proven by cafes, bakeries, ice-cream brands and HORECA — all powered by I'm Ube wholesale powder.
          </p>
        </div>
      </section>

      {/* BEVERAGES */}
      <section className="py-16 md:py-24" data-testid="recipes-beverages">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-12 w-12 rounded-2xl bg-[#4B0082]/10 flex items-center justify-center text-[#4B0082]">
              <GlassWater size={22} />
            </div>
            <div>
              <div className="eyebrow">Section 01</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A]">Beverages</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {BEVERAGES.map((r, i) => <RecipeCard key={r.name} r={r} i={i} />)}
          </div>
        </div>
      </section>

      {/* DESSERTS */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#FCFAF8] to-[#F3EFEA]" data-testid="recipes-desserts">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-12 w-12 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center text-[#B5952F]">
              <Cake size={22} />
            </div>
            <div>
              <div className="eyebrow">Section 02</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A]">Desserts & Bakery</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {DESSERTS.map((r, i) => <RecipeCard key={r.name} r={r} i={i} />)}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h3 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mb-4">Building your own Ube product line?</h3>
          <p className="text-[#555] mb-7">We supply HORECA, D2C and private-label brands. Sample packs available from 1 kg.</p>
          <button onClick={() => openEnquiry()} className="btn-primary" data-testid="recipes-cta">Request Wholesale Pricing</button>
        </div>
      </section>
    </div>
  );
}
